// <storadar-map> — the embeddable store-locator web component (PRD task-031).
//
// A framework-free custom element that renders a synchronized map + list from
// the public widget API (server task-030), inside a shadow root so it neither
// leaks styles onto nor inherits styles from the arbitrary customer page it is
// embedded on.
//
// BOOT ORDER (no layout shift): the reserved height comes from CSS on :host, so
// the box is stable from first paint. connectedCallback paints a skeleton
// immediately, then async boot() loads config, applies the customer theme,
// resolves availability, builds the layout, and lazy-loads MapLibre.
//
// THE MAP FOLLOWS THE RESULTS (F-039, PIVOT1): the query decides what is found and the results
// decide where the camera goes — never the other way round. A search fetches, renders the list,
// and only THEN fits the camera to the bounds of the complete result set (refetch -> applyViewport
// -> fitCameraToResults). The radius select filters; it frames nothing. Zero results leave the
// camera untouched. The visitor can still drag and zoom freely, and their own map moves still
// re-run the query while "search as I move the map" is on — but a camera move the widget itself
// makes never does, or the map would be back in charge of the results.
//
// SEAMS (both documented, both pluggable):
//   * DATA — `dataSource` property, or an inline preview payload (a
//     `data-storadar-preview` attribute, or a <script type="application/json"
//     data-storadar-preview> child), or (default) the live API. This is the
//     preview seam AND the test-fixture seam.
//   * MAP — `createMap` property overrides the lazy MapLibre factory. Default is
//     a dynamic import of ./map/mapChunk (the lazy chunk). Tests inject a fake so
//     they can assert map<->list sync without WebGL/network.
//
// READY SIGNAL (task-036 CI perf harness): once the first real pins/clusters
// render lands (see markReady()), the element gains a `data-storadar-ready`
// attribute and dispatches a bubbling `storadar:ready` CustomEvent. This is the
// widget's own definition of "time to interactive" — nothing outside the
// component should invent a second one (e.g. guessing from a fixed timeout).

import { DataSource, DataSourceError } from "../data/DataSource";
import { LiveDataSource } from "../data/LiveDataSource";
import { PreviewDataSource, type PreviewPayload } from "../data/PreviewDataSource";
import type {
  ConfigPayload,
  LocationDetail,
  SearchCenter,
  ViewportMode,
  ViewportPayload,
  ViewportQuery,
} from "../data/types";
import type { CreateMapController, MapController, MapViewState } from "../map/MapController";
import { DetailPanel } from "../ui/DetailPanel";
import { FilterBar } from "../ui/FilterBar";
import { ListView } from "../ui/ListView";
import { SearchBar, type SearchBarHandlers } from "../ui/SearchBar";
import { emptyState, errorState, mapErrorState, skeletonState, terminalErrorState, unavailableState } from "../ui/states";
import { bboxForRadius, fitViewToBounds, fitZoomForBbox, viewportBboxFor, type CameraView } from "../util/geo";
import { resolveUnit } from "../util/units";
import { baseStyleSheet } from "./styles";

/**
 * Why a viewport fetch is running. It decides three things: whether the results may move the
 * camera (F-039), whether the expensive result-bounds aggregate is requested, and how a failure is
 * surfaced.
 *
 * * `SEARCH` — an autocomplete pick, "use my location", or a radius change. Asks for bounds,
 *   fetches first and fits the camera to what came back. A failure is the visitor's search
 *   failing, so it is shown.
 * * `VIEWPORT` — the initial load, a filter toggle, the search-as-I-move re-query. The visitor is
 *   already looking where they want to look, so the camera is left alone and no bounds are needed.
 *   A failure means there is nothing trustworthy to show, so it is shown.
 * * `LAYER_REFRESH` — the follow-up fetch after a fit crossed the cluster/pin boundary. Pure
 *   enhancement: the list on screen is already correct and already the visitor's answer, so a
 *   failure here is swallowed rather than allowed to replace working UI with an error.
 */
type FetchIntent = "SEARCH" | "VIEWPORT" | "LAYER_REFRESH";

/**
 * Outcome of looking for an inline preview carrier ([readInlinePreview]).
 * "invalid" is deliberately distinct from "absent": a carrier that exists but
 * does not parse means the page author intended a preview, so the boot must
 * fail visibly instead of quietly falling through to the live API.
 */
type InlinePreview = { kind: "absent" } | { kind: "present"; payload: PreviewPayload } | { kind: "invalid" };

/**
 * Origin the widget bundle itself was served from, derived from the entry
 * script's own URL (`import.meta.url`). This is the DEFAULT Storadar API origin
 * when the embed omits an explicit `api-base`: the GA4 model, where the loader
 * self-locates its backend rather than the snippet hardcoding the origin a
 * second time (it is already baked once into the `<script src>`). An explicit
 * `api-base` attribute still overrides this — required only when the script is
 * served from a dedicated CDN whose origin differs from the API's. Falls back to
 * "" (same-origin/root-relative) if the URL can't be parsed.
 */
const SCRIPT_ORIGIN: string = (() => {
  try {
    return new URL(import.meta.url).origin;
  } catch {
    return "";
  }
})();

/** Width (px) at or below which the layout switches to the mobile bottom sheet. */
const MOBILE_BREAKPOINT = 640;
/**
 * DEV-ONLY fallback MapLibre style URL. This is NOT for production: production
 * deployments set PROTOMAPS_TILE_URL server-side (a self-hosted Protomaps style)
 * so it arrives in the config payload's [ConfigPayload.tileSourceUrl]. Style
 * resolution order (see [resolveStyleUrl]): `tiles-url`/`style-url` attribute
 * override > config payload > this dev default.
 */
const DEV_DEFAULT_STYLE_URL = "https://tiles.openfreemap.org/styles/liberty";

export class StoradarMap extends HTMLElement {
  /** Programmatic data-source override (preview/test seam). Set before connect. */
  dataSource: DataSource | null = null;
  /** Map-factory override (test seam). Default lazy-imports the MapLibre chunk. */
  createMap: CreateMapController | null = null;

  private shadow!: ShadowRoot;
  private body!: HTMLElement;
  private config: ConfigPayload | null = null;
  private map: MapController | null = null;
  private list: ListView | null = null;
  private source: DataSource | null = null;
  private searchBar: SearchBar | null = null;
  private filterBar: FilterBar | null = null;
  private detailPanel: DetailPanel | null = null;

  private resizeObserver: ResizeObserver | null = null;
  private inFlight: AbortController | null = null;
  /** Separate in-flight guard for the detail fetch, aborted when superseded. */
  private detailInFlight: AbortController | null = null;
  private firstLoad = true;
  /**
   * The most recently applied viewport payload. Kept so the map, which attaches
   * AFTER the list is already interactive (task-036 progressive boot), can paint
   * the data the list is already showing without a second network round-trip.
   */
  private lastPayload: ViewportPayload | null = null;
  /**
   * The query behind [lastPayload]'s FIRST page — reused (with page+1) to append
   * deeper directory pages on infinite scroll (widget-directory-list). Reassigned
   * to a fresh object on every {@link refetch}, so its reference identity doubles
   * as a generation marker: an in-flight append whose base query was superseded is
   * discarded rather than appended onto a newer viewport.
   */
  private lastQuery: ViewportQuery | null = null;
  /** True while a next-page append fetch is in flight (blocks overlapping appends). */
  private loadingMore = false;
  /**
   * The view (bbox/zoom) used for the most recent fetch. Its purpose is the
   * window BEFORE the map attaches (task-036 progressive boot): if the visitor
   * toggles a filter or runs a search in that window, {@link refetch} has no live
   * map camera to read, so it falls back to this last view rather than silently
   * doing nothing. Once the map is attached, the map's own camera supersedes it.
   */
  private lastView: MapViewState | null = null;
  /**
   * Whether the map has painted its first data (task-036 SECOND, non-gated
   * metric — "map painted"). Distinct from {@link ready}: the widget is
   * interactive (ready) as soon as the list renders; the map catching up is a
   * progressive enhancement we still keep eyes on. Reset on every (re)boot.
   */
  private mapPainted = false;
  /** Guards against a late async result applying after the element is torn down. */
  private disposed = false;
  /**
   * Whether the initial pins/clusters render has happened this boot (task-036 CI
   * perf harness "time to interactive" signal). Reset on every (re)boot so a
   * reconnect/retry re-fires the ready signal — the harness always measures from
   * a fresh navigation, so only the very first paint per boot matters in
   * practice, but staleness here would be a silent false-positive on retry.
   */
  private ready = false;

  // --- Search state (task-032, F-032). IN-MEMORY ONLY — never persisted (GDPR),
  // reset on every reconnect the same as the rest of the component's state.
  /** Active search center from an autocomplete pick or "use my location"; null = unfiltered. */
  private searchCenter: SearchCenter | null = null;
  /**
   * Active search radius (metres) while a [searchCenter] is set; null = none.
   *
   * IT FILTERS, IT DOES NOT FRAME (F-039): this shapes the QUERY bbox client-side (see
   * {@link searchView}) so the server returns the locations within the radius. It has no say in
   * where the camera ends up — that comes from the bounds of whatever the query returned.
   */
  private searchRadius: number | null = null;
  /**
   * The camera fitted to the most recent search's results (F-039), retained for two reasons: a
   * map that attaches AFTER the search (task-036 progressive boot) can land on it instead of the
   * locator's default view, and it is the view a later filter toggle queries from.
   */
  private resultCamera: CameraView | null = null;
  /** The map-pane canvas, retained so a search can size its client-side bbox/zoom. */
  private mapCanvas: HTMLElement | null = null;
  /** The map-pane failure notice while one is showing (F-030: the map fails, the list does not). */
  private mapErrorEl: HTMLElement | null = null;
  // --- Filter state (task-033, F-018). IN-MEMORY ONLY, reset on every reboot.
  /** Tags the visitor has toggled on; empty = unfiltered. */
  private selectedTags: string[] = [];
  /** Whether the open-now filter is active. */
  private openNow = false;

  static get observedAttributes(): string[] {
    return ["height"];
  }

  connectedCallback(): void {
    this.disposed = false;
    if (!this.shadowRoot) {
      this.shadow = this.attachShadow({ mode: "open" });
      this.shadow.adoptedStyleSheets = [baseStyleSheet()];
      this.body = document.createElement("div");
      this.body.className = "root";
      this.shadow.appendChild(this.body);
    }
    this.applyHeightAttribute();
    void this.boot();
  }

  disconnectedCallback(): void {
    this.dispose();
  }

  attributeChangedCallback(name: string): void {
    if (name === "height") {
      this.applyHeightAttribute();
    }
  }

  /** Maps the `height` attribute onto the reserved-height custom property. */
  private applyHeightAttribute(): void {
    const height = this.getAttribute("height");
    if (height) {
      // Accept a bare number as pixels, or any CSS length verbatim.
      const value = /^\d+$/.test(height) ? `${height}px` : height;
      this.style.setProperty("--storadar-height", value);
    }
  }

  /** Full async boot; also the retry entry point (idempotent — rebuilds state). */
  private async boot(): Promise<void> {
    this.teardownLiveState();
    this.render(skeletonState());

    try {
      this.source = this.resolveDataSource();
    } catch (error) {
      // A present-but-unreadable inline preview payload. Terminal by design: the
      // payload is baked into the page, so a retry cannot fix it, and falling
      // through to the live API would silently swap a demo for live data.
      this.renderConfigError(error);
      return;
    }

    let config: ConfigPayload;
    try {
      config = await this.source.loadConfig();
    } catch (error) {
      if (this.disposed) {
        return;
      }
      this.renderConfigError(error);
      return;
    }
    if (this.disposed) {
      return;
    }
    this.config = config;
    this.applyTheme(config);

    // Entitlement UNAVAILABLE: friendly offline state, no map. READ_ONLY_GRACE
    // and FULL both serve normally (grace is visitor-invisible by design).
    if (config.flags.availability === "UNAVAILABLE") {
      this.render(unavailableState());
      return;
    }

    await this.buildLocatorUi(config);
  }

  /**
   * Resolves the map factory: the injected [createMap] test seam if present,
   * otherwise the lazy MapLibre chunk.
   *
   * PROFILED DECISION (task-036): this is invoked AFTER {@link markReady}, not in
   * parallel at boot. The obvious "kick the import off early to overlap the
   * config fetch" is a trap here — resolving `import("../map/mapChunk")` forces
   * V8 to evaluate MapLibre's ~1MB module top-level ON THE MAIN THREAD, which
   * measurably blocks the list render and *delayed* TTI in profiling (readyMs
   * regressed from ~0.4s to ~1.2s). The dynamic import is the code-split point
   * that keeps the initial bundle under the F-030 50KB budget; deferring it past
   * interactivity keeps that whole cost off the TTI critical path. The harness
   * throttles CPU (not network), so parallelising the download buys nothing while
   * the parallel *evaluation* costs everything — hence: import after ready.
   */
  private loadMapFactory(): Promise<CreateMapController> {
    if (this.createMap) {
      return Promise.resolve(this.createMap);
    }
    return import("../map/mapChunk").then((m) => m.createMapController);
  }

  /**
   * Resolves the data source per the documented precedence: an injected
   * [dataSource] property (preview/test) > an inline preview payload (attribute
   * or <script> child) > the live API keyed by the `token` attribute.
   *
   * Throws (non-retryable [DataSourceError]) when a preview carrier is PRESENT
   * but its JSON cannot be parsed. A page that mounted a preview never meant to
   * talk to the live API, so downgrading to [LiveDataSource] here would silently
   * put live data behind what the author believes is a demo — a visible failure
   * is the only honest outcome.
   */
  private resolveDataSource(): DataSource {
    if (this.dataSource) {
      return this.dataSource;
    }
    const inline = this.readInlinePreview();
    if (inline.kind === "invalid") {
      throw new DataSourceError("This page's embedded preview data could not be read.", false);
    }
    if (inline.kind === "present") {
      return new PreviewDataSource(inline.payload);
    }
    const token = this.getAttribute("token") ?? "";
    // Explicit api-base wins; otherwise self-locate to the script's own origin
    // (see SCRIPT_ORIGIN) so a snippet with no api-base still reaches the API.
    const apiBase = this.getAttribute("api-base")?.trim() || SCRIPT_ORIGIN;
    return new LiveDataSource(token, apiBase);
  }

  /**
   * Reads an inline preview payload, from either carrier:
   *
   *   1. a `data-storadar-preview` ATTRIBUTE on the element, holding the JSON;
   *   2. a `<script type="application/json" data-storadar-preview>` child.
   *
   * Both exist because of how the host page is authored, not because the widget
   * wants two seams. A hand-written page (the dev harness) uses the script
   * child, which reads better in source. A page rendered by a server-side HTML
   * DSL cannot: DSL text nodes are HTML-escaped, `"` becomes `&quot;`, and the
   * HTML parser does NOT decode entities inside a raw-text `<script>` element,
   * so the JSON would arrive corrupt. Attribute values survive that round trip
   * intact, which is what lets the marketing page mount a real widget without a
   * raw-HTML escape hatch.
   *
   * A carrier that is present but does not parse (empty included) is "invalid",
   * never "absent": both carriers share this one check, and the caller must
   * fail visibly rather than fall through to live data.
   */
  private readInlinePreview(): InlinePreview {
    const script = this.querySelector<HTMLScriptElement>('script[type="application/json"][data-storadar-preview]');
    if (!this.hasAttribute("data-storadar-preview") && script === null) {
      return { kind: "absent" };
    }
    const json = this.getAttribute("data-storadar-preview") ?? script?.textContent ?? "";
    try {
      return { kind: "present", payload: JSON.parse(json) as PreviewPayload };
    } catch {
      return { kind: "invalid" };
    }
  }

  /** Applies the customer's BRAND_THEME CSS variables onto the host (inline). */
  private applyTheme(config: ConfigPayload): void {
    for (const [name, value] of Object.entries(config.theme.cssVariables)) {
      // Only accept our namespaced contract vars; ignore anything unexpected so a
      // stray key can't set arbitrary host inline styles.
      if (name.startsWith("--storadar-")) {
        this.style.setProperty(name, value);
      }
    }
  }

  /**
   * Builds the search bar + map + list layout and reaches interactivity.
   *
   * PROGRESSIVE BOOT (task-036 TTI): the list + search + filters render and
   * become interactive from the FIRST data fetch — which is keyed to the
   * locator's configured default view (computed deterministically, not read off
   * the async map camera) — and {@link markReady} fires THEN, before MapLibre
   * has finished parsing/compiling. The map is attached afterwards as a
   * progressive enhancement over the already-interactive list (see
   * {@link attachMap}); its arrival is a SECOND, non-gated metric. This is why
   * TTI is "list interactive", not "map painted": the visitor can search, arrow
   * through results, open a location, and read distances the instant the list is
   * up — the map tiles are a richer view layered on top, not a precondition.
   */
  private async buildLocatorUi(config: ConfigPayload): Promise<void> {
    const shell = document.createElement("div");
    shell.className = "shell";

    const layout = document.createElement("div");
    layout.className = "layout";

    const unit = resolveUnit(config.locator.units, navigator.language);
    this.list = new ListView(config.locator.name, unit, navigator.language, {
      onHover: (id) => this.map?.highlightPin(id),
      onSelect: (id) => this.onSelectLocation(id),
      onLoadMore: () => void this.loadMoreLocations(),
    });

    this.searchBar = new SearchBar(this.source!, unit, this.searchBarHandlers());

    // Always instantiate the filter bar: the open-now toggle is meaningful even
    // when a locator declares no tags (the chip row is then simply empty).
    this.filterBar = new FilterBar(config.tags, {
      onFiltersChange: (tags, openNow) => {
        this.selectedTags = tags;
        this.openNow = openNow;
        void this.refetch();
      },
    });

    this.detailPanel = new DetailPanel({ onClose: () => {} });

    const mapPane = document.createElement("div");
    mapPane.className = "map-pane";
    const mapCanvas = document.createElement("div");
    mapCanvas.className = "map-canvas";
    this.mapCanvas = mapCanvas;
    mapPane.appendChild(mapCanvas);
    this.appendBadge(config, mapPane);

    layout.append(this.list.element, mapPane);
    // The detail panel is an overlay over the whole shell (positioned absolute).
    shell.append(this.searchBar.element, this.filterBar.element, layout, this.detailPanel.element);
    this.render(shell);

    this.observeSize();

    // FIRST fetch, keyed to the locator's configured default view computed
    // deterministically from config (no dependency on the not-yet-attached map's
    // camera). Renders the list + facet counts and fires the ready signal. Every
    // MapLibre cost — chunk evaluation, WebGL construction — is deliberately kept
    // AFTER this point (see loadMapFactory's kdoc), so nothing about the map sits
    // on the critical path to interactivity.
    await this.refetch(this.defaultView(config, mapCanvas));

    // Let the browser PAINT the now-interactive list and let observers/input see
    // the ready signal before MapLibre's construction monopolises the main thread
    // (task-036). `new maplibregl.Map()` is a long, un-chunkable synchronous task;
    // running it in the same frame as markReady would block the very paint that
    // proves interactivity — inflating any externally-measured TTI and janking
    // the first frame. Yielding a frame here costs nothing real (the list is
    // already up) and keeps the map strictly a follow-on enhancement.
    await this.yieldToPaint();

    // Now load + construct + attach the map over the interactive list
    // (progressive enhancement; second, non-gated metric).
    await this.attachMap(mapCanvas, config);
  }

  /**
   * Resolves after the browser has COMPOSITED a frame (and thus run any
   * rAF-based observers, e.g. the perf harness's readiness poll) — the seam that
   * keeps the following long synchronous task (MapLibre construction) from
   * starving the frame that renders and reveals interactivity. Two chained
   * `requestAnimationFrame`s guarantee at least one full, unblocked frame between
   * markReady and the construction: a single frame proved racy (the poll could
   * land just before markReady, then get starved by the construction on the next
   * frame), intermittently inflating externally-measured TTI. The trailing
   * `setTimeout` hops to a fresh macrotask so the heavy work can't run inside the
   * frame callback itself.
   */
  private yieldToPaint(): Promise<void> {
    return new Promise((resolve) => {
      let settled = false;
      const done = () => {
        if (settled) {
          return;
        }
        settled = true;
        resolve();
      };
      if (typeof requestAnimationFrame === "function") {
        requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(done, 0)));
        // rAF is fully PAUSED in a hidden/background tab (a store page opened in a
        // background tab, restored on startup, or prerendered). Without a floor the
        // rAF chain above never fires and the map attachment hangs FOREVER — the
        // visitor gets an interactive list but a permanently blank map pane. This
        // timeout still fires (throttled) while hidden, so boot always proceeds and
        // the map attaches; in a visible tab the rAF chain resolves first and this
        // is a no-op. The yield is a paint optimization, never a hard dependency.
        setTimeout(done, 300);
      } else {
        setTimeout(done, 0);
      }
    });
  }

  /**
   * The map's initial view derived from the locator config — the deterministic
   * seed for the first viewport fetch so the list can render before the map. The
   * bbox is the Web-Mercator box the map WILL show at (defaultLat, defaultLng,
   * defaultZoom) for the current map-pane size; if the pane isn't laid out yet
   * (0×0), we fall back to the host's box, then to a sensible default, so the
   * box is never degenerate.
   */
  private defaultView(config: ConfigPayload, mapCanvas: HTMLElement): MapViewState {
    const rect = mapCanvas.getBoundingClientRect();
    const width = rect.width || this.clientWidth || 800;
    const height = rect.height || this.clientHeight || 600;
    const center = { lat: config.locator.defaultLat, lng: config.locator.defaultLng };
    const zoom = config.locator.defaultZoom;
    return { bbox: viewportBboxFor(center, zoom, width, height), zoom, center };
  }

  /**
   * Loads the map chunk (its first, deferred use), constructs the map, then
   * paints the already-fetched data onto it (no second network round-trip) and
   * fires the SECOND, non-gated "map painted" metric. Runs AFTER {@link markReady}
   * so the heavy chunk evaluation + WebGL construction never delay interactivity —
   * they only layer the map over the already-usable list.
   */
  private async attachMap(mapCanvas: HTMLElement, config: ConfigPayload): Promise<void> {
    let map: MapController;
    try {
      const factory = await this.loadMapFactory();
      if (this.disposed || !this.list) {
        // Torn down, or the first fetch resolved the locator to a terminal state
        // (empty/error) that tore the live list down — nothing to pair a map with.
        return;
      }
      map = await this.instantiateMap(mapCanvas, config, factory);
    } catch {
      if (this.disposed || !this.list) {
        return;
      }
      // The map library genuinely failed to load (offline/CDN). Confine the failure to the pane
      // that failed: this method's entire contract is that the map is an ENHANCEMENT layered over
      // an already-interactive list (see buildLocatorUi), so tearing the whole shell down here
      // would take a working store locator — search, filters, distances, directions — away from
      // the visitor because of the one part they can manage without. Honest and visible, but
      // proportionate: an inline notice in the map area, with a retry for the map alone.
      this.showMapError(mapCanvas, config);
      return;
    }
    this.clearMapError();
    if (this.disposed || !this.list) {
      map.destroy();
      return;
    }
    this.map = map;
    // Replay a search that happened BEFORE the map attached (task-036): land the freshly-attached
    // map on the camera already fitted to those results, so it frames what the list is showing
    // rather than the locator's default view. No refetch — the list is already correct; this is
    // purely the map catching up to it.
    if (this.resultCamera) {
      this.map.setCamera(this.resultCamera.center, this.resultCamera.zoom);
    }
    if (this.lastPayload) {
      this.paintMap(this.lastPayload);
    }
    this.markMapPainted();
  }

  /**
   * Shows the map-pane failure notice over [mapCanvas] and wires its retry back to
   * {@link attachMap}, so retrying re-attempts ONLY the map — the list is already correct and must
   * not be re-fetched or re-rendered to fix a map problem.
   */
  private showMapError(mapCanvas: HTMLElement, config: ConfigPayload): void {
    this.clearMapError();
    const notice = mapErrorState(() => {
      this.clearMapError();
      void this.attachMap(mapCanvas, config);
    });
    this.mapErrorEl = notice;
    // Into the PANE, not the canvas: MapLibre owns the canvas element's children once it mounts.
    (mapCanvas.parentElement ?? mapCanvas).appendChild(notice);
  }

  /** Removes the map-pane failure notice, if one is showing. */
  private clearMapError(): void {
    this.mapErrorEl?.remove();
    this.mapErrorEl = null;
  }

  /** Pushes a viewport payload's clusters/pins onto the map (the list is rendered separately). */
  private paintMap(payload: ViewportPayload): void {
    if (!this.map) {
      return;
    }
    if (payload.mapMode === "CLUSTERS") {
      this.map.showClusters(payload.clusters ?? []);
    } else {
      // At pin zoom the directory rows ARE the map's pins (they carry lat/lng).
      this.map.showPins(payload.locations);
    }
  }

  /** Constructs the live map from an already-resolved factory. */
  private async instantiateMap(
    container: HTMLElement,
    config: ConfigPayload,
    factory: CreateMapController,
  ): Promise<MapController> {
    const reducedMotion =
      typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
    const styleUrl = this.resolveStyleUrl(config);

    return factory(
      {
        container,
        center: { lat: config.locator.defaultLat, lng: config.locator.defaultLng },
        zoom: config.locator.defaultZoom,
        styleUrl,
        reducedMotion,
      },
      {
        // Zooming into a cluster is a request for the finer data underneath it, which lives at
        // the new zoom level — so this is the one camera move that still re-queries once it
        // settles (F-039: everywhere else, search drives the map, never the reverse).
        onClusterZoomSettled: () => void this.refetch(),
        onClusterClick: (cluster) => this.map?.zoomToward(cluster.lat, cluster.lng),
        onPinHover: (id) => this.list?.highlight(id),
        onPinSelect: (id) => this.onSelectLocation(id),
      },
    );
  }

  /**
   * Resolves the MapLibre style URL by precedence: an explicit per-embed
   * `tiles-url`/`style-url` attribute override > the server config payload's
   * deployment-controlled [ConfigPayload.tileSourceUrl] > the dev-only default.
   */
  private resolveStyleUrl(config: ConfigPayload): string {
    const attribute = this.getAttribute("tiles-url") ?? this.getAttribute("style-url");
    if (attribute && attribute.trim() !== "") {
      return attribute;
    }
    if (config.tileSourceUrl && config.tileSourceUrl.trim() !== "") {
      return config.tileSourceUrl;
    }
    return DEV_DEFAULT_STYLE_URL;
  }

  /**
   * Fetches a viewport and reflects it onto the list (and the map, once it's
   * attached). [viewOverride] supplies the view for the FIRST fetch — before the
   * map exists — computed from config (see {@link defaultView}); afterwards the
   * live map's own camera is the source of truth. The list, not the map, is the
   * hard dependency: this is what lets the widget become interactive before
   * MapLibre loads (task-036).
   *
   * RESULTS FIRST, CAMERA SECOND (F-039): with [intent] `"SEARCH"` the payload is rendered onto
   * the list and only THEN is the camera fitted to the bounds that came back (see
   * {@link fitCameraToResults}). The ordering is the feature, not an implementation detail — the
   * list is the authority on what was found and the map illustrates it.
   */
  private async refetch(viewOverride?: MapViewState, intent: FetchIntent = "VIEWPORT"): Promise<void> {
    if (!this.list || !this.source) {
      return;
    }
    // An explicit view (the initial default view, or a search's client-computed
    // radius view) wins; otherwise the live map camera (pan/zoom refetches);
    // otherwise the last view we fetched (keeps filter toggles working before the
    // map attaches). Deriving search/initial views explicitly is what lets the
    // widget query correctly before the map is attached (task-036).
    //
    // A MOVING CAMERA IS NOT A VIEWPORT: mid-ease (a results fit) or mid-drag, getView() returns
    // an interpolated box the visitor never actually looked at, so a filter toggle that lands in
    // that window would query a frame that existed for one tick. While the camera is unsettled we
    // use the last view we deliberately fetched — after a fit that IS the fitted view, and during
    // a drag the moveend re-query is already coming to correct it.
    const settledView = this.map && !this.map.isMoving() ? this.map.getView() : null;
    const view = viewOverride ?? settledView ?? this.lastView;
    if (!view) {
      return;
    }
    this.lastView = view;
    this.inFlight?.abort();
    const controller = new AbortController();
    this.inFlight = controller;

    const query: ViewportQuery = {
      bbox: view.bbox,
      zoom: view.zoom,
      // Tag/open-now filters (task-033): the FilterBar owns this state and
      // triggers a refetch on every toggle (instant apply, no Apply button).
      tags: this.selectedTags,
      openNow: this.openNow,
      // F-032: when a search is active, the server re-sorts by distance to this center and
      // returns the result bounds the camera is fitted to (F-039). There is no server-side
      // radius param — the radius is expressed entirely by the bbox above (see searchView).
      center: this.searchCenter,
      // Only a search fits the camera, so only a search pays for the extent aggregate (F-039).
      bounds: intent === "SEARCH",
      page: 0,
      pageSize: null,
    };
    // A fresh object each refetch: its identity is the generation marker a
    // concurrent infinite-scroll append checks to avoid appending a stale page.
    this.lastQuery = query;

    let payload: ViewportPayload;
    try {
      payload = await this.source.loadViewport(query, controller.signal);
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return; // Superseded by a newer move; not a failure.
      }
      if (this.disposed) {
        return;
      }
      if (intent === "LAYER_REFRESH") {
        // A background enhancement failed. The list on screen came from a fetch that SUCCEEDED and
        // is still the visitor's correct answer; replacing it with an error would punish them for
        // a request they never made. The map keeps drawing the resolution it already has — a
        // slightly coarser map beside a correct list, which is the proportionate failure here.
        return;
      }
      this.renderViewportError(error);
      return;
    }
    if (this.disposed || controller.signal.aborted) {
      return;
    }
    // LIST FIRST...
    this.applyViewport(payload);
    // ...THEN the camera, and only for a search (F-039).
    if (intent === "SEARCH") {
      this.fitCameraToResults(payload);
    }
  }

  /**
   * Frames the camera on the search results that just rendered (F-039) — the whole point of the
   * epic: a new query is a full reset of BOTH center and zoom, derived from the bounds of the
   * complete result set the server reported, never from the radius the visitor picked.
   *
   * ZERO RESULTS LEAVE THE CAMERA ALONE. The server sends no bounds when nothing matched (never a
   * zeroed box), and moving the map to frame nothing would be both disorienting and dishonest —
   * the list's widen-radius state carries that message instead.
   */
  private fitCameraToResults(payload: ViewportPayload): void {
    const bounds = payload.resultBounds;
    if (!bounds) {
      // Nothing matched, so there is no camera for this search — and, crucially, the PREVIOUS
      // search's camera must not stay queued: a map attaching later would otherwise fly to results
      // that are no longer on the list, framing an answer the visitor was just told doesn't exist.
      this.resultCamera = null;
      return;
    }
    if (!this.mapCanvas) {
      return;
    }

    const rect = this.mapCanvas.getBoundingClientRect();
    const width = rect.width || this.clientWidth || 800;
    const height = rect.height || this.clientHeight || 600;
    const camera = fitViewToBounds(bounds, width, height);
    this.resultCamera = camera;
    // The camera now frames the results, so it — not the radius box we queried with — is the view
    // a later filter toggle or a late-attaching map works from.
    this.lastView = {
      bbox: viewportBboxFor(camera.center, camera.zoom, width, height),
      zoom: camera.zoom,
      center: camera.center,
    };
    this.map?.setCamera(camera.center, camera.zoom);

    // A wide search is queried at a zoom where the server aggregates into clusters. If the results
    // turn out to sit close together, the fit can drop the visitor the far side of the server's
    // cluster/pin boundary — cluster bubbles at a zoom where individual pins belong (or, the other
    // way, pins where the server would now aggregate). Refresh the map layer only when the frame
    // the visitor actually got would be SERVED DIFFERENTLY, which is a question only
    // config.clusterZoomMax can answer: a zoom-delta heuristic gets it wrong precisely at the
    // boundary (query 11.8 -> clusters, fit 12.2 -> pins is a crossing no delta rule catches).
    if (this.modeForZoom(camera.zoom) !== payload.mapMode) {
      // LAYER_REFRESH, never SEARCH: it cannot move the camera again, so it cannot loop — and if
      // it fails, the list the visitor already has stays exactly as it is.
      void this.refetch(this.lastView, "LAYER_REFRESH");
    }
  }

  /**
   * How the server would draw the MAP at [zoom] — clusters strictly below the configured
   * boundary, pins at or above it (mirrors WidgetDataService).
   *
   * Returns null when the boundary is unknown, which happens only against a config payload
   * predating the field (a stale CDN entry). Unknown means unknown: the caller then leaves the map
   * alone rather than guessing a boundary and re-fetching on a coin flip.
   */
  private modeForZoom(zoom: number): ViewportMode | null {
    const boundary = this.config?.clusterZoomMax;
    if (typeof boundary !== "number" || !Number.isFinite(boundary)) {
      return null;
    }
    return zoom < boundary ? "CLUSTERS" : "PINS";
  }

  /**
   * Infinite scroll (widget-directory-list): fetches the NEXT directory page for
   * the current viewport and appends it to the list. Additive — it never touches
   * the map (clusters/pins are the viewport's, not paginated) and never aborts the
   * main viewport fetch. Discarded if the viewport changed while paging (the base
   * query's identity is checked after the await).
   */
  private async loadMoreLocations(): Promise<void> {
    const baseQuery = this.lastQuery;
    if (!this.list || !this.source || !baseQuery || this.loadingMore) {
      return;
    }
    if (!this.lastPayload?.hasMore) {
      return;
    }
    this.loadingMore = true;
    // A deeper page never fits the camera, so it never asks for the extent aggregate either —
    // even when the base query was a search that did.
    const nextQuery: ViewportQuery = { ...baseQuery, bounds: false, page: (this.lastPayload.page ?? 0) + 1 };
    const controller = new AbortController();
    let payload: ViewportPayload;
    try {
      payload = await this.source.loadViewport(nextQuery, controller.signal);
    } catch {
      // A failed append is non-fatal: keep the rows already shown, drop the guard
      // so a later scroll can retry. No error state — the directory is still usable.
      this.loadingMore = false;
      return;
    }
    this.loadingMore = false;
    // Stale-guard: a pan/zoom/filter/search refetched (new lastQuery object) while
    // we paged — this page belongs to a viewport the visitor left. Discard it.
    if (this.disposed || this.lastQuery !== baseQuery || !this.lastPayload) {
      return;
    }
    // Fold the appended rows into lastPayload so a further page (and a map that
    // attaches late) sees the full set; carry the new page/hasMore forward.
    this.lastPayload = {
      ...payload,
      locations: [...this.lastPayload.locations, ...payload.locations],
    };
    this.list.appendLocations(payload.locations, payload.hasMore);
  }

  /** Routes a viewport payload to the right render mode and handles emptiness. */
  private applyViewport(payload: ViewportPayload): void {
    // Remember the latest data so a map attaching AFTER the list is interactive
    // (task-036 progressive boot) can paint it without a second fetch.
    this.lastPayload = payload;
    const empty = payload.locations.length === 0 && (payload.clusters?.length ?? 0) === 0;

    // F-032: an active search matching nothing is a DIFFERENT truth than "this
    // locator has no locations" — offer to widen rather than a dead end, and
    // keep the map/search bar mounted (the visitor may want to keep exploring).
    if (empty && this.searchCenter && this.searchBar) {
      this.map?.showPins([]);
      this.list?.renderNoResultsInRadius(this.searchBar.currentRadiusLabel(), () => this.onWidenRadius());
      return;
    }

    // A locator whose HOME view has nothing is genuinely empty — say so plainly,
    // once. The map/list are replaced by the empty state, so release the map
    // instance (its container is about to be detached) rather than orphan it.
    if (this.firstLoad && empty) {
      this.firstLoad = false;
      this.map?.destroy();
      this.map = null;
      this.list = null;
      this.searchBar = null;
      this.filterBar = null;
      this.detailPanel = null;
      this.render(emptyState(this.config?.locator.name ?? "This locator"));
      return;
    }
    this.firstLoad = false;

    // The MAP branches on mapMode; the DIRECTORY always lists individual rows
    // (widget-directory-list) — no "zoom in to see locations" dead end.
    if (payload.mapMode === "CLUSTERS") {
      this.map?.showClusters(payload.clusters ?? []);
    } else {
      this.map?.showPins(payload.locations);
    }
    this.list?.renderLocations(payload.locations, payload.filters, payload.hasMore, payload.scanCapReached);
    // Refresh the chip facet counts to the current viewport (task-033).
    this.filterBar?.updateCounts(payload.tagCounts ?? []);
    this.markReady();
  }

  /**
   * Marks the widget interactive (task-036 CI perf harness TTI signal): fires
   * once per boot, right after the FIRST real pins/clusters render lands on the
   * LIST — i.e. the earliest moment a visitor could plausibly search, arrow
   * through results, open a location, or read distances. Honest by construction:
   * this is the exact code path {@link applyViewport} already takes to paint the
   * list, not a separate timer or a guess.
   *
   * TTI = INTERACTIVE, NOT MAP-PAINTED (task-036 definition change, documented in
   * perf/lib/tti.mjs + README): the map is a progressive enhancement layered on
   * once MapLibre finishes loading (see {@link markMapPainted}); the widget is
   * genuinely usable — list, keyboard nav, search, detail — before the map paints,
   * so gating TTI on the map penalised a widget that was already interactive. The
   * map-painted moment is still measured, just not gated.
   *
   * Exposed two ways so the harness (or any embedder) can observe it without
   * polyfills:
   *   * `data-storadar-ready="true"` attribute on the host element (poll/observe).
   *   * a bubbling, composed `storadar:ready` CustomEvent (escapes the shadow root).
   */
  private markReady(): void {
    if (this.ready) {
      return;
    }
    this.ready = true;
    this.setAttribute("data-storadar-ready", "true");
    this.dispatchEvent(new CustomEvent("storadar:ready", { bubbles: true, composed: true }));
  }

  /**
   * Fires the SECOND, non-gated metric once the map has painted its first
   * clusters/pins (task-036): the map became a live, interactive surface layered
   * over the already-ready list. Kept visible to the harness so a regression in
   * map-paint time is caught even though TTI no longer waits on it. Exposed as
   * `data-storadar-map-ready="true"` + a bubbling `storadar:map-painted` event.
   */
  private markMapPainted(): void {
    if (this.mapPainted) {
      return;
    }
    this.mapPainted = true;
    this.setAttribute("data-storadar-map-ready", "true");
    this.dispatchEvent(new CustomEvent("storadar:map-painted", { bubbles: true, composed: true }));
  }

  /**
   * Selecting a pin/row keeps the map<->list highlight in sync (task-031) AND
   * opens the detail panel (task-033), fetching the location's full detail.
   */
  private onSelectLocation(id: string): void {
    this.map?.selectPin(id);
    this.list?.select(id);
    void this.openDetail(id);
  }

  /**
   * Fetches and renders a location's detail. Aborts any prior in-flight detail
   * fetch, and on failure shows an honest inline error with a retry inside the
   * panel — a detail fetch problem must never crash or blank the widget.
   */
  private async openDetail(id: string): Promise<void> {
    if (!this.detailPanel || !this.source) {
      return;
    }
    this.detailInFlight?.abort();
    const controller = new AbortController();
    this.detailInFlight = controller;

    const panel = this.detailPanel;
    panel.open();

    let detail: LocationDetail;
    try {
      detail = await this.source.loadDetail(id, controller.signal);
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return; // Superseded by a newer selection.
      }
      if (this.disposed || controller.signal.aborted) {
        return;
      }
      const message = error instanceof Error ? error.message : "We couldn't load this location.";
      panel.renderDetailError(message, () => void this.openDetail(id));
      return;
    }
    if (this.disposed || controller.signal.aborted) {
      return;
    }
    panel.renderDetail(detail);
  }

  /** Wires the search box's actions onto the results-first search flow (F-039). */
  private searchBarHandlers(): SearchBarHandlers {
    return {
      onSearchCenter: (center, radiusMeters) => {
        this.searchCenter = center;
        this.searchRadius = radiusMeters;
        this.applySearch();
      },
      onRadiusChange: (radiusMeters) => this.applyRadius(radiusMeters),
      onClearSearch: () => {
        this.searchCenter = null;
        this.searchRadius = null;
        // Clearing the box is not a new query — it widens what is listed for the area the
        // visitor is already looking at, so the camera stays exactly where they left it.
        this.resultCamera = null;
        void this.refetch();
      },
    };
  }

  /**
   * A radius change re-runs the CURRENT search with a wider/narrower query box (F-039). It moves
   * no camera of its own: the new results come back and the camera follows them, exactly as it
   * would for a fresh query.
   */
  private applyRadius(radiusMeters: number): void {
    if (this.searchCenter) {
      this.searchRadius = radiusMeters;
      this.applySearch();
    }
  }

  /**
   * Runs the active search: query first, camera after (F-039).
   *
   * The query bbox is computed CLIENT-SIDE from the search center + radius (see
   * {@link searchView}) — that is the radius doing its one job, filtering — and it is what makes
   * search work BEFORE the map has loaded (task-036). Nothing here touches the camera; the fit
   * happens in {@link fitCameraToResults} once the results (and their bounds) are in.
   */
  private applySearch(): void {
    void this.refetch(this.searchView() ?? undefined, "SEARCH");
  }

  /**
   * The QUERY view for the active search — never the camera (F-039). It is the [bboxForRadius]
   * box around the search center, at the zoom that box would fit the map pane ([fitZoomForBbox])
   * so the server picks the right cluster/pin mode for it. Null when no search is active or the
   * pane size is unknown, in which case callers fall back to the map/last view.
   */
  private searchView(): MapViewState | null {
    if (!this.searchCenter || this.searchRadius == null || !this.mapCanvas) {
      return null;
    }
    const rect = this.mapCanvas.getBoundingClientRect();
    const width = rect.width || this.clientWidth || 800;
    const height = rect.height || this.clientHeight || 600;
    const bbox = bboxForRadius(this.searchCenter, this.searchRadius);
    return { bbox, zoom: fitZoomForBbox(bbox, width, height), center: this.searchCenter };
  }

  /** The empty-search-result "widen radius" affordance (F-032). */
  private onWidenRadius(): void {
    const widened = this.searchBar?.widenRadius();
    if (widened != null) {
      this.applyRadius(widened);
    }
  }

  /**
   * "Powered by Storadar" attribution (F-107, task-034).
   *
   * MECHANISM: the canonical embed snippet (server EmbedSnippet.kt) ships the
   * badge anchor as a genuine light-DOM child of `<storadar-map>` — that's what
   * makes it crawlable pre-JS (curl-verifiable, no script execution needed).
   * This method does NOT create that anchor. It creates a shadow-DOM
   * `<slot name="badge">` that PROJECTS the light-DOM anchor into the layout:
   * what a crawler reads in the raw response body is exactly what a visitor
   * sees once the component boots. A shadow-only badge that duplicates/hides a
   * separate light-DOM node would be cloaking-adjacent (crawler view diverging
   * from user view on purpose) — deliberately rejected in favor of this "one
   * true anchor, just relocated by CSS" approach.
   *
   * The slot carries fallback content (a locally-built anchor) for embeds that
   * predate this snippet shape or omit the light-DOM anchor by mistake — never
   * a substitute for it once the customer adopts the canonical snippet.
   *
   * badge_hidden (server-enforced SCALE-only, F-107): honored by hiding the
   * SLOT via [config.flags.badgeHidden] only — never by reading any attribute
   * off this element, so a tampered/spoofed attribute on the host can't hide a
   * badge the server didn't authorize.
   */
  private appendBadge(config: ConfigPayload, mapPane: HTMLElement): void {
    const slot = document.createElement("slot");
    slot.name = "badge";
    slot.className = "badge-slot";
    slot.appendChild(this.buildFallbackBadge());
    slot.hidden = config.flags.badgeHidden;
    mapPane.appendChild(slot);
  }

  /** Fallback badge rendered only when no light-DOM anchor is assigned to the slot. */
  private buildFallbackBadge(): HTMLAnchorElement {
    const badge = document.createElement("a");
    badge.className = "badge";
    badge.textContent = "Powered by Storadar";
    badge.href = this.badgeHref();
    badge.target = "_blank";
    badge.rel = "noopener noreferrer";
    return badge;
  }

  /** Mirrors EmbedSnippet.kt's badgeUrl — kept here only for the fallback-badge case. */
  private badgeHref(): string {
    const token = this.getAttribute("token");
    return token
      ? `https://storadar.com/?utm_source=badge&ref=${encodeURIComponent(token)}`
      : "https://storadar.com/";
  }

  /** ResizeObserver toggles the mobile class (shadow-DOM friendly, no cookies). */
  private observeSize(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        this.classList.toggle("is-mobile", entry.contentRect.width <= MOBILE_BREAKPOINT);
      }
    });
    this.resizeObserver.observe(this);
  }

  private renderConfigError(error: unknown): void {
    if (error instanceof DataSourceError && !error.retryable) {
      this.render(terminalErrorState(error.message));
      return;
    }
    const message = error instanceof Error ? error.message : "Something went wrong.";
    this.render(errorState(message, () => void this.boot(), retryDelayOf(error)));
  }

  /**
   * Surfaces a failed viewport fetch WITHOUT dismantling the widget.
   *
   * WHY THIS IS NOT A FULL-PANE STATE (the bug this replaces): the retryable branch used to
   * `render()` an error over the whole shell, which detaches the live ListView. `this.list` still
   * pointed at that orphaned element, so a retry that SUCCEEDED rendered its rows into a node no
   * longer in the document — a permanently dead "Try again" after any transient blip. The error
   * now goes inside the directory, so the search bar, filters and map stay where they are, the
   * retry re-renders into a list that is still attached, and recovery is genuinely recovery.
   *
   * The non-retryable branch still replaces everything, and should: a 404 means the token is gone,
   * so there is no locator left to keep on screen.
   */
  private renderViewportError(error: unknown): void {
    const retryable = !(error instanceof DataSourceError) || error.retryable;
    const message = error instanceof Error ? error.message : "Something went wrong.";
    if (!retryable) {
      this.render(terminalErrorState(message));
      return;
    }
    if (!this.list) {
      // No live directory to put the message in (the very first fetch failed into a torn-down
      // shell, or a terminal state already replaced it) — fall back to the full-pane state, whose
      // retry re-boots and therefore rebuilds a usable shell.
      this.render(errorState(message, () => void this.boot(), retryDelayOf(error)));
      return;
    }
    this.list.renderFetchError(message, () => void this.refetch(), retryDelayOf(error));
  }

  /** Replaces the shadow body with a single state/subtree. */
  private render(node: HTMLElement): void {
    this.body.replaceChildren(node);
  }

  /** Tears down transient per-boot state without detaching the element. */
  private teardownLiveState(): void {
    this.inFlight?.abort();
    this.inFlight = null;
    this.detailInFlight?.abort();
    this.detailInFlight = null;
    this.map?.destroy();
    this.map = null;
    this.list = null;
    this.searchBar = null;
    this.filterBar = null;
    this.detailPanel = null;
    this.firstLoad = true;
    this.lastPayload = null;
    this.ready = false;
    this.mapPainted = false;
    this.removeAttribute("data-storadar-ready");
    this.removeAttribute("data-storadar-map-ready");
    // IN-MEMORY ONLY search + filter state resets with every (re)boot — never persisted (GDPR).
    this.searchCenter = null;
    this.searchRadius = null;
    this.resultCamera = null;
    this.clearMapError();
    this.mapCanvas = null;
    this.selectedTags = [];
    this.openNow = false;
  }

  /** Full teardown on disconnect: release the map, observer and any in-flight work. */
  private dispose(): void {
    this.disposed = true;
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.teardownLiveState();
  }
}

/** The server-advised retry delay (429 Retry-After), or null when not advised. */
function retryDelayOf(error: unknown): number | null {
  return error instanceof DataSourceError ? error.retryAfterSeconds : null;
}
